import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { createClient } from "@supabase/supabase-js";
import type { Database } from "@/integrations/supabase/types";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

function publicClient() {
  return createClient<Database>(
    process.env.SUPABASE_URL!,
    process.env.SUPABASE_PUBLISHABLE_KEY!,
    { auth: { storage: undefined, persistSession: false, autoRefreshToken: false } },
  );
}

function slugify(input: string) {
  return input
    .toLowerCase()
    .replace(/å|ä/g, "a")
    .replace(/ö/g, "o")
    .replace(/é/g, "e")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 70) || "inlagg";
}

type Author = { display_name: string | null; avatar_url: string | null; badge?: string | null };

async function authorsFor(
  supabase: ReturnType<typeof publicClient>,
  ids: string[],
): Promise<Record<string, Author>> {
  const unique = Array.from(new Set(ids)).filter(Boolean);
  if (!unique.length) return {};
  const { data } = await supabase
    .from("public_profiles")
    .select("id, display_name, avatar_url, badge")
    .in("id", unique);
  const map: Record<string, Author> = {};
  for (const p of data ?? [])
    map[p.id] = { display_name: p.display_name, avatar_url: p.avatar_url, badge: p.badge };
  return map;
}


const POST_SELECT =
  "id, slug, title, body, post_type, image_urls, pinned, locked, vote_score, comment_count, last_activity_at, created_at, edited_at, user_id, category_id";

/* ----------------------- SPAM & RATE LIMIT GUARDS ----------------------- */

const ALLOWED_LINK_HOSTS = ["akhusbilfritid.se", "akhusbilfritid.com", "fyndanu.se", "blocket.se"];

function externalLinks(text: string) {
  const matches = text.match(/https?:\/\/[^\s)]+/gi) ?? [];
  return matches.filter((url) => {
    try {
      const host = new URL(url).hostname.replace(/^www\./, "").toLowerCase();
      return !ALLOWED_LINK_HOSTS.some((h) => host === h || host.endsWith(`.${h}`));
    } catch {
      return true;
    }
  });
}

/** Nya konton får inte spamma länkar, och ingen får posta i rasande takt. */
async function assertTrustedContribution(
  supabase: any,
  userId: string,
  text: string,
  kind: "post" | "comment",
) {
  const sinceIso = new Date(Date.now() - (kind === "post" ? 60 : 10) * 60_000).toISOString();
  const table = kind === "post" ? "forum_posts" : "forum_comments";
  const { count } = await supabase
    .from(table)
    .select("id", { count: "exact", head: true })
    .eq("user_id", userId)
    .gte("created_at", sinceIso);
  const cap = kind === "post" ? 3 : 10;
  if ((count ?? 0) >= cap) {
    throw new Error(
      kind === "post"
        ? "Du har startat många trådar den senaste timmen. Ta en pause och försök igen snart."
        : "Du svarar väldigt snabbt just nu. Vänta några minuter innan nästa svar.",
    );
  }

  if (externalLinks(text).length) {
    const [{ count: posts }, { count: comments }] = await Promise.all([
      supabase.from("forum_posts").select("id", { count: "exact", head: true }).eq("user_id", userId),
      supabase.from("forum_comments").select("id", { count: "exact", head: true }).eq("user_id", userId),
    ]);
    const contributions = (posts ?? 0) + (comments ?? 0);
    if (contributions < 3) {
      throw new Error(
        "Nya medlemmar kan inte lägga externa länkar. Skriv några inlägg först — då släpps länkarna på.",
      );
    }
  }
}

/** Plockar ut @omnämnanden ur en text, inkl. namnvarianter med 1–3 ord. */
function mentionNames(text: string) {
  const raw = text.match(/@([\p{L}0-9][\p{L}0-9 _.-]{1,38})/gu) ?? [];
  const out = new Set<string>();
  for (const m of raw) {
    const cleaned = m.slice(1).trim().replace(/[.,:;!?]+$/, "");
    const words = cleaned.split(/\s+/).filter(Boolean);
    for (let i = 1; i <= Math.min(3, words.length); i++) {
      const variant = words.slice(0, i).join(" ");
      if (variant.length >= 2) out.add(variant);
    }
  }
  return Array.from(out).slice(0, 12);
}



/* ------------------------------ CATEGORIES ------------------------------ */

export const listForumCategories = createServerFn({ method: "GET" }).handler(async () => {
  const supabase = publicClient();
  const { data, error } = await supabase
    .from("forum_categories")
    .select("id, slug, name, description, icon, color, sort_order")
    .order("sort_order", { ascending: true });
  if (error) throw new Error(error.message);

  const cats = data ?? [];
  const counts: Record<string, number> = {};
  const latest: Record<string, { title: string; slug: string; created_at: string }> = {};
  if (cats.length) {
    const { data: posts } = await supabase
      .from("forum_posts")
      .select("category_id, title, slug, created_at")
      .order("created_at", { ascending: false })
      .limit(1000);
    for (const p of posts ?? []) {
      counts[p.category_id] = (counts[p.category_id] ?? 0) + 1;
      if (!latest[p.category_id]) latest[p.category_id] = { title: p.title, slug: p.slug, created_at: p.created_at };
    }
  }

  return {
    categories: cats.map((c) => ({
      ...c,
      post_count: counts[c.id] ?? 0,
      latest: latest[c.id] ?? null,
    })),
  };
});

/* -------------------------------- POSTS -------------------------------- */

export const listForumPosts = createServerFn({ method: "GET" })
  .inputValidator((input) =>
    z
      .object({
        category: z.string().trim().max(80).optional(),
        sort: z.enum(["hot", "new", "top", "unanswered"]).optional(),
        type: z.enum(["discussion", "question", "trip"]).optional(),
        limit: z.number().int().min(1).max(100).optional(),
        offset: z.number().int().min(0).max(5000).optional(),
      })
      .parse(input ?? {}),
  )
  .handler(async ({ data }) => {
    const supabase = publicClient();
    const limit = data.limit ?? 40;
    const offset = data.offset ?? 0;

    let categoryId: string | null = null;
    let category: { id: string; slug: string; name: string; description: string | null; icon: string | null; color: string | null } | null = null;
    if (data.category) {
      const { data: cat } = await supabase
        .from("forum_categories")
        .select("id, slug, name, description, icon, color")
        .eq("slug", data.category)
        .maybeSingle();
      if (!cat) return { posts: [], category: null, total: 0, hasMore: false };
      category = cat;
      categoryId = cat.id;
    }

    let q = supabase
      .from("forum_posts")
      .select(POST_SELECT, { count: "exact" })
      .range(offset, offset + limit - 1);
    if (categoryId) q = q.eq("category_id", categoryId);
    if (data.type) q = q.eq("post_type", data.type);

    q = q.order("pinned", { ascending: false });
    if (data.sort === "top") q = q.order("vote_score", { ascending: false }).order("created_at", { ascending: false });
    else if (data.sort === "new") q = q.order("created_at", { ascending: false });
    else if (data.sort === "unanswered") q = q.eq("comment_count", 0).order("created_at", { ascending: false });
    else q = q.order("last_activity_at", { ascending: false }).order("vote_score", { ascending: false });

    const { data: rows, error, count } = await q;
    if (error) throw new Error(error.message);

    const authors = await authorsFor(supabase, (rows ?? []).map((r) => r.user_id));
    const { data: cats } = await supabase.from("forum_categories").select("id, slug, name, color");
    const catById = Object.fromEntries((cats ?? []).map((c) => [c.id, c]));

    return {
      category,
      total: count ?? (rows ?? []).length,
      hasMore: offset + (rows ?? []).length < (count ?? 0),
      posts: (rows ?? []).map((p) => ({
        ...p,
        author: authors[p.user_id] ?? { display_name: null, avatar_url: null },
        category: catById[p.category_id] ?? null,
      })),
    };

  });

export const getForumPost = createServerFn({ method: "GET" })
  .inputValidator((input) => z.object({ slug: z.string().trim().min(1).max(120) }).parse(input))
  .handler(async ({ data }) => {
    const supabase = publicClient();
    const { data: post, error } = await supabase
      .from("forum_posts")
      .select(POST_SELECT)
      .eq("slug", data.slug)
      .maybeSingle();
    if (error) throw new Error(error.message);
    if (!post) return { post: null, comments: [], category: null, related: [] };

    const { data: category } = await supabase
      .from("forum_categories")
      .select("id, slug, name, description, color, icon")
      .eq("id", post.category_id)
      .maybeSingle();

    const { data: comments } = await supabase
      .from("forum_comments")
      .select("id, post_id, parent_id, user_id, body, image_urls, is_best_answer, vote_score, created_at, edited_at")
      .eq("post_id", post.id)
      .order("is_best_answer", { ascending: false })
      .order("vote_score", { ascending: false })
      .order("created_at", { ascending: true })
      .limit(500);

    const authors = await authorsFor(supabase, [
      post.user_id,
      ...(comments ?? []).map((c) => c.user_id),
    ]);

    const { data: related } = await supabase
      .from("forum_posts")
      .select("id, slug, title, comment_count, vote_score")
      .eq("category_id", post.category_id)
      .neq("id", post.id)
      .order("last_activity_at", { ascending: false })
      .limit(5);

    return {
      post: { ...post, author: authors[post.user_id] ?? { display_name: null, avatar_url: null } },
      category: category ?? null,
      comments: (comments ?? []).map((c) => ({
        ...c,
        author: authors[c.user_id] ?? { display_name: null, avatar_url: null },
      })),
      related: related ?? [],
    };
  });

export const createForumPost = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) =>
    z
      .object({
        category_id: z.string().uuid(),
        title: z.string().trim().min(6).max(160),
        body: z.string().trim().min(10).max(20000),
        post_type: z.enum(["discussion", "question", "trip"]).default("discussion"),
        image_paths: z.array(z.string().trim().max(400)).max(8).optional(),
      })
      .parse(input),
  )
  .handler(async ({ context, data }) => {
    const { supabase, userId } = context;
    await assertTrustedContribution(supabase, userId, `${data.title} ${data.body}`, "post");
    const slug = `${slugify(data.title)}-${Math.random().toString(36).slice(2, 7)}`;
    const { data: row, error } = await supabase
      .from("forum_posts")
      .insert({
        category_id: data.category_id,
        user_id: userId,
        slug,
        title: data.title,
        body: data.body,
        post_type: data.post_type,
        image_urls: (data.image_paths ?? []).filter((p) => p.startsWith("forum/")),
      })
      .select("id, slug")
      .single();
    if (error) throw new Error(error.message);
    return { slug: row.slug, post_id: row.id };
  });

export const updateForumPost = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) =>
    z
      .object({
        id: z.string().uuid(),
        title: z.string().trim().min(6).max(160),
        body: z.string().trim().min(10).max(20000),
      })
      .parse(input),
  )
  .handler(async ({ context, data }) => {
    const { supabase, userId } = context;
    if (externalLinks(`${data.title} ${data.body}`).length) {
      const [{ count: posts }, { count: comments }] = await Promise.all([
        supabase.from("forum_posts").select("id", { count: "exact", head: true }).eq("user_id", userId),
        supabase.from("forum_comments").select("id", { count: "exact", head: true }).eq("user_id", userId),
      ]);
      if ((posts ?? 0) + (comments ?? 0) < 3) {
        throw new Error("Nya medlemmar kan inte lägga externa länkar ännu.");
      }
    }
    const { error } = await supabase
      .from("forum_posts")
      .update({ title: data.title, body: data.body, edited_at: new Date().toISOString() })
      .eq("id", data.id)
      .eq("user_id", userId);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const deleteForumPost = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => z.object({ id: z.string().uuid() }).parse(input))
  .handler(async ({ context, data }) => {
    const { error } = await context.supabase.from("forum_posts").delete().eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

/* ------------------------------- COMMENTS ------------------------------- */

export const addForumComment = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) =>
    z
      .object({
        post_id: z.string().uuid(),
        parent_id: z.string().uuid().nullable().optional(),
        body: z.string().trim().min(2).max(10000),
      })
      .parse(input),
  )
  .handler(async ({ context, data }) => {
    const { supabase, userId } = context;
    const { data: post } = await supabase
      .from("forum_posts")
      .select("id, locked")
      .eq("id", data.post_id)
      .maybeSingle();
    if (!post) throw new Error("Inlägget finns inte");
    if (post.locked) throw new Error("Tråden är låst");

    await assertTrustedContribution(supabase, userId, data.body, "comment");

    const { data: inserted, error } = await supabase
      .from("forum_comments")
      .insert({
        post_id: data.post_id,
        parent_id: data.parent_id ?? null,
        user_id: userId,
        body: data.body,
      })
      .select("id")
      .single();
    if (error) throw new Error(error.message);

    // @omnämnanden -> notiser (körs via server-klient för att hålla rättigheterna strama)
    const names = mentionNames(data.body);
    if (names.length) {
      try {
        const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
        await supabaseAdmin.rpc("forum_notify_mentions", {
          _comment_id: inserted.id,
          _names: names,
        });
      } catch (err) {
        console.error("mention notify failed", err);
      }
    }

    // E-post till bevakare (misslyckas tyst)
    try {
      const { notifyThreadSubscribers } = await import("@/lib/forum-notify.server");
      await notifyThreadSubscribers({
        postId: data.post_id,
        commentId: inserted.id,
        actorId: userId,
        body: data.body,
      });
    } catch (err) {
      console.error("subscriber email failed", err);
    }

    return { ok: true, comment_id: inserted.id };
  });

export const updateForumComment = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) =>
    z.object({ id: z.string().uuid(), body: z.string().trim().min(2).max(10000) }).parse(input),
  )
  .handler(async ({ context, data }) => {
    const { supabase, userId } = context;
    const { error } = await supabase
      .from("forum_comments")
      .update({ body: data.body, edited_at: new Date().toISOString() })
      .eq("id", data.id)
      .eq("user_id", userId);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const deleteForumComment = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => z.object({ id: z.string().uuid() }).parse(input))
  .handler(async ({ context, data }) => {
    const { error } = await context.supabase.from("forum_comments").delete().eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });


export const markBestAnswer = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) =>
    z.object({ post_id: z.string().uuid(), comment_id: z.string().uuid() }).parse(input),
  )
  .handler(async ({ context, data }) => {
    const { supabase, userId } = context;
    const { data: post } = await supabase
      .from("forum_posts")
      .select("id, user_id")
      .eq("id", data.post_id)
      .maybeSingle();
    if (!post || post.user_id !== userId) throw new Error("Bara trådskaparen kan välja bästa svar");

    await supabase.from("forum_comments").update({ is_best_answer: false }).eq("post_id", data.post_id);
    const { error } = await supabase
      .from("forum_comments")
      .update({ is_best_answer: true })
      .eq("id", data.comment_id)
      .eq("post_id", data.post_id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

/* --------------------------------- VOTES -------------------------------- */

export const castForumVote = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) =>
    z
      .object({
        post_id: z.string().uuid().nullable().optional(),
        comment_id: z.string().uuid().nullable().optional(),
        value: z.union([z.literal(1), z.literal(-1), z.literal(0)]),
      })
      .refine((v) => !!v.post_id !== !!v.comment_id, "Ange antingen inlägg eller svar")
      .parse(input),
  )
  .handler(async ({ context, data }) => {
    const { supabase, userId } = context;
    const column = data.post_id ? "post_id" : "comment_id";
    const target = (data.post_id ?? data.comment_id) as string;

    if (data.value === 0) {
      const { error } = await supabase
        .from("forum_votes")
        .delete()
        .eq("user_id", userId)
        .eq(column, target);
      if (error) throw new Error(error.message);
      return { ok: true, value: 0 };
    }

    const { data: existing } = await supabase
      .from("forum_votes")
      .select("id")
      .eq("user_id", userId)
      .eq(column, target)
      .maybeSingle();

    if (existing) {
      const { error } = await supabase
        .from("forum_votes")
        .update({ value: data.value })
        .eq("id", existing.id);
      if (error) throw new Error(error.message);
    } else {
      const { error } = await supabase.from("forum_votes").insert({
        user_id: userId,
        post_id: data.post_id ?? null,
        comment_id: data.comment_id ?? null,
        value: data.value,
      });
      if (error) throw new Error(error.message);
    }
    return { ok: true, value: data.value };
  });

export const myForumVotes = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) =>
    z.object({ post_id: z.string().uuid().optional() }).parse(input ?? {}),
  )
  .handler(async ({ context }) => {
    const { supabase, userId } = context;
    const { data, error } = await supabase
      .from("forum_votes")
      .select("post_id, comment_id, value")
      .eq("user_id", userId)
      .limit(2000);
    if (error) throw new Error(error.message);
    const posts: Record<string, number> = {};
    const comments: Record<string, number> = {};
    for (const v of data ?? []) {
      if (v.post_id) posts[v.post_id] = v.value;
      if (v.comment_id) comments[v.comment_id] = v.value;
    }
    return { posts, comments };
  });

/* -------------------------------- REPORTS ------------------------------- */

export const reportForumContent = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) =>
    z
      .object({
        post_id: z.string().uuid().nullable().optional(),
        comment_id: z.string().uuid().nullable().optional(),
        reason: z.string().trim().min(2).max(80),
        details: z.string().trim().max(1000).optional(),
      })
      .refine((v) => !!v.post_id !== !!v.comment_id, "Ange antingen inlägg eller svar")
      .parse(input),
  )
  .handler(async ({ context, data }) => {
    const { supabase, userId } = context;
    const { error } = await supabase.from("forum_reports").insert({
      reporter_id: userId,
      post_id: data.post_id ?? null,
      comment_id: data.comment_id ?? null,
      reason: data.reason,
      details: data.details ?? null,
    });
    if (error) throw new Error(error.message);
    return { ok: true };
  });

/* --------------------------------- IMAGES ------------------------------- */

export const signForumImages = createServerFn({ method: "POST" })
  .inputValidator((input) =>
    z
      .object({
        paths: z.array(z.string().trim().max(400)).max(24),
      })
      .parse(input),
  )
  .handler(async ({ data }) => {
    const paths = data.paths.filter((p) => p.startsWith("forum/"));
    if (!paths.length) return { urls: {} as Record<string, string> };
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const urls: Record<string, string> = {};
    const { data: signed } = await supabaseAdmin.storage
      .from("user-content")
      .createSignedUrls(paths, 60 * 60 * 6);
    for (const s of signed ?? []) {
      if (s.path && s.signedUrl) urls[s.path] = s.signedUrl;
    }
    return { urls };
  });

/* ------------------------------- PROFILES ------------------------------- */

export const getForumMember = createServerFn({ method: "GET" })
  .inputValidator((input) => z.object({ id: z.string().uuid() }).parse(input))
  .handler(async ({ data }) => {
    const supabase = publicClient();
    const [{ data: profile }, { data: posts }, { data: comments }] = await Promise.all([
      supabase.from("public_profiles").select("id, display_name, avatar_url").eq("id", data.id).maybeSingle(),
      supabase
        .from("forum_posts")
        .select("id, slug, title, post_type, vote_score, comment_count, created_at")
        .eq("user_id", data.id)
        .order("created_at", { ascending: false })
        .limit(30),
      supabase
        .from("forum_comments")
        .select("id, post_id, body, vote_score, is_best_answer, created_at")
        .eq("user_id", data.id)
        .order("created_at", { ascending: false })
        .limit(100),
    ]);
    if (!profile) throw new Error("Medlemmen finns inte");

    const postList = posts ?? [];
    const commentList = comments ?? [];
    const karma =
      postList.reduce((s, p) => s + (p.vote_score ?? 0), 0) +
      commentList.reduce((s, c) => s + (c.vote_score ?? 0), 0);
    const bestAnswers = commentList.filter((c) => c.is_best_answer).length;

    const badges: { label: string; hint: string }[] = [];
    if (postList.length >= 1) badges.push({ label: "Trådstartare", hint: "Har startat minst en tråd" });
    if (commentList.length >= 5) badges.push({ label: "Hjälpsam", hint: "5+ svar i forumet" });
    if (bestAnswers >= 1) badges.push({ label: "Expert", hint: `${bestAnswers} bästa svar` });
    if (karma >= 25) badges.push({ label: "Uppröstad", hint: "25+ karma" });
    if (postList.some((p) => p.post_type === "trip")) badges.push({ label: "Resenär", hint: "Har delat en resa" });

    return {
      member: { id: profile.id, display_name: profile.display_name, avatar_url: profile.avatar_url },
      stats: { karma, posts: postList.length, comments: commentList.length, bestAnswers },
      badges,
      posts: postList,
    };
  });

/* ------------------------------ MODERATION ------------------------------ */

async function assertForumAdmin(supabase: {
  rpc: (fn: "is_current_user_admin") => PromiseLike<{ data: unknown }>;
}) {
  const { data } = await supabase.rpc("is_current_user_admin");
  if (!data) throw new Error("Endast admin");
}

export const listForumReports = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => z.object({ resolved: z.boolean().default(false) }).parse(input ?? {}))
  .handler(async ({ context, data }) => {
    const { supabase } = context;
    await assertForumAdmin(supabase);
    const { data: reports, error } = await supabase
      .from("forum_reports")
      .select("id, reporter_id, post_id, comment_id, reason, details, resolved, created_at")
      .eq("resolved", data.resolved)
      .order("created_at", { ascending: false })
      .limit(200);
    if (error) throw new Error(error.message);

    const rows = reports ?? [];
    const postIds = rows.map((r) => r.post_id).filter(Boolean) as string[];
    const commentIds = rows.map((r) => r.comment_id).filter(Boolean) as string[];

    const [{ data: posts }, { data: comments }] = await Promise.all([
      postIds.length
        ? supabase.from("forum_posts").select("id, slug, title, locked, pinned").in("id", postIds)
        : Promise.resolve({ data: [] as never[] }),
      commentIds.length
        ? supabase.from("forum_comments").select("id, post_id, body").in("id", commentIds)
        : Promise.resolve({ data: [] as never[] }),
    ]);

    return {
      reports: rows.map((r) => ({
        ...r,
        post: (posts ?? []).find((p) => p.id === r.post_id) ?? null,
        comment: (comments ?? []).find((c) => c.id === r.comment_id) ?? null,
      })),
    };
  });

export const resolveForumReport = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) =>
    z.object({ id: z.string().uuid(), resolved: z.boolean().default(true) }).parse(input),
  )
  .handler(async ({ context, data }) => {
    await assertForumAdmin(context.supabase);
    const { error } = await context.supabase
      .from("forum_reports")
      .update({ resolved: data.resolved })
      .eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const moderateForumPost = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) =>
    z
      .object({
        id: z.string().uuid(),
        pinned: z.boolean().optional(),
        locked: z.boolean().optional(),
        remove: z.boolean().optional(),
      })
      .parse(input),
  )
  .handler(async ({ context, data }) => {
    const { supabase } = context;
    await assertForumAdmin(supabase);
    if (data.remove) {
      const { error } = await supabase.from("forum_posts").delete().eq("id", data.id);
      if (error) throw new Error(error.message);
      return { ok: true };
    }
    const patch: { pinned?: boolean; locked?: boolean } = {};
    if (typeof data.pinned === "boolean") patch.pinned = data.pinned;
    if (typeof data.locked === "boolean") patch.locked = data.locked;
    const { error } = await supabase.from("forum_posts").update(patch).eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const moderateForumComment = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => z.object({ id: z.string().uuid() }).parse(input))
  .handler(async ({ context, data }) => {
    await assertForumAdmin(context.supabase);
    const { error } = await context.supabase.from("forum_comments").delete().eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const checkForumAdmin = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data } = await context.supabase.rpc("is_current_user_admin");
    return { isAdmin: !!data };
  });

/* --------------------------- SEARCH & DISCOVERY --------------------------- */

export const searchForumPosts = createServerFn({ method: "GET" })
  .inputValidator((input) =>
    z.object({ q: z.string().trim().min(2).max(120), limit: z.number().int().min(1).max(50).optional() }).parse(input),
  )
  .handler(async ({ data }) => {
    const supabase = publicClient();
    const term = data.q.replace(/[%,]/g, " ").trim();
    const { data: rows, error } = await supabase
      .from("forum_posts")
      .select(POST_SELECT)
      .or(`title.ilike.%${term}%,body.ilike.%${term}%`)
      .order("vote_score", { ascending: false })
      .order("last_activity_at", { ascending: false })
      .limit(data.limit ?? 30);
    if (error) throw new Error(error.message);

    const authors = await authorsFor(supabase, (rows ?? []).map((r) => r.user_id));
    const { data: cats } = await supabase.from("forum_categories").select("id, slug, name, color");
    const catById = Object.fromEntries((cats ?? []).map((c) => [c.id, c]));

    return {
      query: data.q,
      posts: (rows ?? []).map((p) => ({
        ...p,
        author: authors[p.user_id] ?? { display_name: null, avatar_url: null },
        category: catById[p.category_id] ?? null,
      })),
    };
  });

export const listUnansweredQuestions = createServerFn({ method: "GET" })
  .inputValidator((input) => z.object({ limit: z.number().int().min(1).max(20).optional() }).parse(input ?? {}))
  .handler(async ({ data }) => {
    const supabase = publicClient();
    const { data: rows, error } = await supabase
      .from("forum_posts")
      .select("id, slug, title, created_at, comment_count, post_type")
      .eq("post_type", "question")
      .eq("comment_count", 0)
      .order("created_at", { ascending: false })
      .limit(data.limit ?? 6);
    if (error) throw new Error(error.message);
    return { questions: rows ?? [] };
  });

export const forumLeaderboard = createServerFn({ method: "GET" })
  .inputValidator((input) => z.object({ limit: z.number().int().min(1).max(20).optional() }).parse(input ?? {}))
  .handler(async ({ data }) => {
    const supabase = publicClient();
    const [{ data: posts }, { data: comments }] = await Promise.all([
      supabase.from("forum_posts").select("user_id, vote_score").limit(2000),
      supabase.from("forum_comments").select("user_id, vote_score, is_best_answer").limit(4000),
    ]);

    const agg: Record<string, { karma: number; posts: number; comments: number; best: number }> = {};
    const bump = (id: string) => (agg[id] ??= { karma: 0, posts: 0, comments: 0, best: 0 });
    for (const p of posts ?? []) {
      const a = bump(p.user_id);
      a.karma += p.vote_score ?? 0;
      a.posts += 1;
    }
    for (const c of comments ?? []) {
      const a = bump(c.user_id);
      a.karma += c.vote_score ?? 0;
      a.comments += 1;
      if (c.is_best_answer) a.best += 1;
    }

    const top = Object.entries(agg)
      .map(([id, v]) => ({ id, ...v, score: v.karma + v.best * 5 + v.comments }))
      .sort((a, b) => b.score - a.score)
      .slice(0, data.limit ?? 8);

    const authors = await authorsFor(supabase, top.map((t) => t.id));
    return {
      members: top.map((t) => ({
        ...t,
        display_name: authors[t.id]?.display_name ?? null,
        avatar_url: authors[t.id]?.avatar_url ?? null,
      })),
    };
  });

/* ----------------------------- NOTIFICATIONS ---------------------------- */

export const listForumNotifications = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase, userId } = context;
    const { data, error } = await supabase
      .from("forum_notifications")
      .select("id, type, post_slug, post_title, excerpt, comment_id, read_at, created_at, actor_id")
      .eq("user_id", userId)
      .order("created_at", { ascending: false })
      .limit(30);
    if (error) throw new Error(error.message);
    const rows = data ?? [];
    const authors = await authorsFor(publicClient(), rows.map((r) => r.actor_id ?? "").filter(Boolean));
    return {
      unread: rows.filter((r) => !r.read_at).length,
      items: rows.map((r) => ({
        ...r,
        actor_name: r.actor_id ? authors[r.actor_id]?.display_name ?? null : null,
      })),
    };
  });

export const markForumNotificationsRead = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) =>
    z.object({ id: z.string().uuid().optional() }).parse(input ?? {}),
  )
  .handler(async ({ context, data }) => {
    const { supabase, userId } = context;
    let q = supabase
      .from("forum_notifications")
      .update({ read_at: new Date().toISOString() })
      .eq("user_id", userId)
      .is("read_at", null);
    if (data.id) q = q.eq("id", data.id);
    const { error } = await q;
    if (error) throw new Error(error.message);
    return { ok: true };
  });
